import React, { useCallback, useState, useEffect } from "react";
import { Card, Table, Button, Row, Col, Space, Tag, Modal, Form, Input, InputNumber, Typography, Spin } from "antd";
import { PlusOutlined, DollarOutlined, HistoryOutlined, GiftOutlined } from "@ant-design/icons";
import { customBaseUrl, httpGet, httpPost } from "../../../../store/http";
import { hideLoader, showLoader } from "../../../../utils/loader";
import { NotificationManager } from "react-notifications";
import moment from "moment";
import Swal from "sweetalert2";

const { Title, Text } = Typography;

export const CustomerRewards = ({ id, activeTab }) => {
  const [rewardData, setRewardData] = useState({
    totalRewardBalance: 0,
    totalGrants: 0,
    activeGrants: 0
  });
  const [balanceLoading, setBalanceLoading] = useState(false);
  
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 0, pageSize: 10, total: 0 });
  
  const [isCreditModalVisible, setIsCreditModalVisible] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);
  const [form] = Form.useForm();

  // Fetch Reward Balance
  const fetchRewardBalance = useCallback(async () => {
    if (!id) return;
    setBalanceLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/reward/balance/${id}`);
      if (res?.success && res.data) {
        setRewardData({
          totalRewardBalance: res.data.totalRewardBalance || 0,
          totalGrants: res.data.totalGrants || 0,
          activeGrants: res.data.activeGrants || 0
        });
      } else {
        setRewardData({
          totalRewardBalance: 0,
          totalGrants: 0,
          activeGrants: 0
        });
      }
    } catch (err) {
      console.error("Error fetching reward balance:", err);
      setRewardData({
        totalRewardBalance: 0,
        totalGrants: 0,
        activeGrants: 0
      });
    } finally {
      setBalanceLoading(false);
    }
  }, [id]);

  // Fetch Reward Grants History
  const fetchRewardGrants = useCallback(async (page = 0) => {
    if (!id) return;
    setGrantsLoading(true);
    try {
      const res = await httpGet(
        `${customBaseUrl.tranv2}/api/v1/admin/reward/grants/${id}?page=${page}&size=${pagination.pageSize}`
      );
      if (res?.success) {
        const content = res.data?.content || res.data || [];
        setGrants(Array.isArray(content) ? content : []);
        setPagination(prev => ({
          ...prev,
          current: page,
          total: res.data?.totalElements || (Array.isArray(content) ? content.length : 0),
        }));
      } else {
        setGrants([]);
      }
    } catch (err) {
      console.error("Error fetching reward grants:", err);
      setGrants([]);
    } finally {
      setGrantsLoading(false);
    }
  }, [id, pagination.pageSize]);

  const fetchAllData = useCallback(() => {
    fetchRewardBalance();
    fetchRewardGrants(0);
  }, [fetchRewardBalance, fetchRewardGrants]);

  // Load data when tab becomes active
  useEffect(() => {
    if (activeTab === "rewards") {
      fetchAllData();
    }
  }, [activeTab, fetchAllData]);

  // Open Credit Modal and prefill fields
  const handleOpenCreditModal = () => {
    let adminEmail = localStorage.getItem("email");
    if (!adminEmail) {
      try {
        const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
        adminEmail = userDetails?.user?.email || userDetails?.email;
      } catch (e) {
        console.error("Failed to parse userDetails from localStorage", e);
      }
    }
    if (!adminEmail) {
      adminEmail = "ADMIN";
    }

    const generatedRef = `REW-MAN-${Date.now()}-${Math.floor(1000 + Math.random() * 9000)}`;
    
    form.resetFields();
    form.setFieldsValue({
      allowedUsage: "ANY",
      expiryDays: 30,
      transactionReference: generatedRef,
      createdBy: adminEmail,
    });
    setIsCreditModalVisible(true);
  };

  // Submit Override Credit Form
  const handleCreditSubmit = async (values) => {
    setCreditLoading(true);
    showLoader();
    try {
      let adminEmail = localStorage.getItem("email");
      if (!adminEmail) {
        try {
          const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
          adminEmail = userDetails?.user?.email || userDetails?.email;
        } catch (e) {
          console.error("Failed to parse userDetails from localStorage", e);
        }
      }
      if (!adminEmail) {
        adminEmail = "ADMIN";
      }

      const payload = {
        userId: parseInt(id, 10),
        amount: parseFloat(values.amount),
        allowedUsage: values.allowedUsage,
        expiryDays: values.expiryDays ? parseInt(values.expiryDays, 10) : 0,
        reason: values.reason,
        transactionReference: values.transactionReference,
        createdBy: adminEmail,
      };

      const res = await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/reward/credit`, payload);
      if (res?.success || res?.status) {
        NotificationManager.success("Reward manually credited successfully!");
        setIsCreditModalVisible(false);
        fetchAllData();
      } else {
        NotificationManager.error(res?.message || "Failed to credit reward.");
      }
    } catch (err) {
      console.error("Error crediting reward manually:", err);
      NotificationManager.error("An error occurred while crediting reward.");
    } finally {
      setCreditLoading(false);
      hideLoader();
    }
  };

  const handleRevokeGrant = async (record) => {
    const { value: reason } = await Swal.fire({
      title: 'Revoke Reward Grant',
      input: 'textarea',
      inputLabel: 'Reason for Revocation',
      inputPlaceholder: 'Enter reason for revoking...',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return 'You need to provide a reason for revocation!';
        }
      }
    });

    if (reason) {
      showLoader();
      try {
        const url = `${customBaseUrl.tranv2}/api/v1/admin/reward/revoke/${record.id}?reason=${encodeURIComponent(reason)}`;
        const res = await httpPost(url, {});
        if (res?.success || res?.status || res?.message?.toLowerCase()?.includes('success')) {
          NotificationManager.success("Reward grant revoked successfully!");
          fetchAllData();
        } else {
          NotificationManager.error(res?.message || "Failed to revoke reward grant.");
        }
      } catch (err) {
        console.error("Error revoking reward grant:", err);
        NotificationManager.error("An error occurred while revoking reward grant.");
      } finally {
        hideLoader();
      }
    }
  };

  const columns = [
    {
      title: "Date Granted",
      dataIndex: "grantedAt",
      key: "grantedAt",
      render: (val, record) => {
        const date = val || record.createdAt;
        return date ? moment(date).format("LLL") : "—";
      },
    },
    {
      title: "Transaction Ref",
      dataIndex: "transactionReference",
      key: "transactionReference",
      render: (val) => <Text copyable={{ text: val }}>{val || "—"}</Text>,
    },
    {
      title: "Trigger Ref",
      dataIndex: "triggerReference",
      key: "triggerReference",
      render: (val) => val ? <Text copyable={{ text: val }}>{val}</Text> : "—",
    },
    {
      title: "Reward Account",
      dataIndex: "rewardAccountNumber",
      key: "rewardAccountNumber",
      render: (val) => val || "—",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (val) => (
        <Text strong style={{ color: "#52c41a" }}>
          ₦{parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: "Grant Balance",
      dataIndex: "balance",
      key: "balance",
      render: (val) => (
        <Text strong style={{ color: "#ff6700" }}>
          ₦{parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: "Allowed Usage",
      dataIndex: "allowedUsage",
      key: "allowedUsage",
      render: (val) => <Tag color="blue">{val || "ANY"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => {
        const color = val === "GRANTED" ? "success" : "warning";
        return <Tag color={color}>{val || "UNKNOWN"}</Tag>;
      },
    },
    {
      title: "Expiry",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (val, record) => {
        const expiry = val || record.expiryDate;
        return expiry ? <Tag color="volcano">{moment(expiry).format("LLL")}</Tag> : <Tag color="default">Never Expires</Tag>;
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (val) => val || "—",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const isRevocable = record.status === "GRANTED" && parseFloat(record.balance || 0) > 0;
        return (
          <Button
            type="danger"
            size="small"
            onClick={() => handleRevokeGrant(record)}
            disabled={!isRevocable}
          >
            Revoke
          </Button>
        );
      }
    }
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Top Metrics Cards Section */}
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{
              borderRadius: 16,
              border: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            {balanceLoading ? (
              <div style={{ padding: "10px 0", textAlign: "center" }}><Spin size="small" /></div>
            ) : (
              <div>
                <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, display: "block" }}>
                  Reward Balance
                </Text>
                <Title level={3} style={{ margin: "4px 0 0 0", color: "#ff6700" }}>
                  ₦{parseFloat(rewardData.totalRewardBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Title>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} md={5}>
          <Card
            style={{
              borderRadius: 16,
              border: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            {balanceLoading ? (
              <div style={{ padding: "10px 0", textAlign: "center" }}><Spin size="small" /></div>
            ) : (
              <div>
                <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, display: "block" }}>
                  Active Grants
                </Text>
                <Title level={3} style={{ margin: "4px 0 0 0" }}>
                  {rewardData.activeGrants || 0}
                </Title>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} md={5}>
          <Card
            style={{
              borderRadius: 16,
              border: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            {balanceLoading ? (
              <div style={{ padding: "10px 0", textAlign: "center" }}><Spin size="small" /></div>
            ) : (
              <div>
                <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, display: "block" }}>
                  Total Grants
                </Text>
                <Title level={3} style={{ margin: "4px 0 0 0" }}>
                  {rewardData.totalGrants || 0}
                </Title>
              </div>
            )}
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={8} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreditModal}
            style={{
              borderRadius: 8,
              backgroundColor: "#ff6700",
              borderColor: "#ff6700",
              height: 44,
              color: "#fff",
              fontWeight: 500,
            }}
          >
            Credit Reward Manually
          </Button>
        </Col>
      </Row>

      {/* Grant History Table */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <HistoryOutlined style={{ color: "#ff6700" }} />
            <span>Reward Grant History</span>
          </div>
        }
        style={{
          borderRadius: 16,
          border: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <Table
          dataSource={grants}
          columns={columns}
          loading={grantsLoading}
          rowKey={(record) => record.id || record.transactionReference || Math.random().toString()}
          pagination={{
            current: pagination.current + 1,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page) => fetchRewardGrants(page - 1),
          }}
          size="small"
        />
      </Card>

      {/* Credit Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <GiftOutlined style={{ color: "#ff6700" }} />
            <span>Manual Reward Credit (Admin Override)</span>
          </div>
        }
        open={isCreditModalVisible}
        onCancel={() => setIsCreditModalVisible(false)}
        footer={null}
        destroyOnClose
        centered
        width={500}
        className="premium-modal"
      >
        <Form form={form} layout="vertical" onFinish={handleCreditSubmit} style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Amount (₦)"
                rules={[
                  { required: true, message: "Please input amount" },
                  { type: "number", min: 0.01, message: "Minimum reward amount is ₦0.01" }
                ]}
              >
                <InputNumber style={{ width: "100%" }} placeholder="e.g. 500" min={0.01} precision={2} />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="expiryDays"
                label="Expiry Days"
                rules={[{ required: true, message: "Please enter expiry days (0 for never)" }]}
              >
                <InputNumber style={{ width: "100%" }} placeholder="e.g. 30" min={0} precision={0} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="allowedUsage"
            label="Allowed Usage"
            rules={[{ required: true, message: "Please input allowed usage" }]}
          >
            <Input placeholder="e.g. ANY, BILLS, TRANSFER" />
          </Form.Item>

          <Form.Item
            name="transactionReference"
            label="Transaction Reference"
            rules={[{ required: true, message: "Please input reference" }]}
          >
            <Input placeholder="Enter unique transaction reference" />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason / Remarks"
            rules={[{ required: true, message: "Please enter a reason" }]}
          >
            <Input.TextArea rows={3} placeholder="Explain why this reward is being credited manually..." />
          </Form.Item>

          <Form.Item name="createdBy" label="Created By" hidden>
            <Input />
          </Form.Item>

          <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
            <Button style={{ flex: 1, borderRadius: 8 }} onClick={() => setIsCreditModalVisible(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={creditLoading}
              style={{
                flex: 1,
                borderRadius: 8,
                backgroundColor: "#ff6700",
                borderColor: "#ff6700",
                color: "#fff",
              }}
            >
              Confirm Credit
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
